'use client'

import {
    Anamnesis,
    getAnamnesis,
    MEDICAL_SPECIALTY_LABELS,
} from '@/lib/api/prontuario/prontuario-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdAssignment,
    MdEdit,
    MdPerson,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioNavigate, useProntuarioParams } from './prontuario-context'

const systemsReviewLabels: Record<string, string> = {
    general: 'Geral',
    cardiovascular: 'Cardiovascular',
    respiratory: 'Respiratório',
    gastrointestinal: 'Gastrointestinal',
    genitourinary: 'Geniturinário',
    musculoskeletal: 'Musculoesquelético',
    neurological: 'Neurológico',
    psychiatric: 'Psiquiátrico',
    skin: 'Pele e Anexos',
    endocrine: 'Endócrino',
    hematologic: 'Hematológico',
}

const physicalExamLabels: Record<string, string> = {
    generalAppearance: 'Ectoscopia',
    vitalSigns: 'Sinais Vitais',
    head: 'Cabeça',
    neck: 'Pescoço',
    chest: 'Tórax',
    abdomen: 'Abdome',
    extremities: 'Extremidades',
    neurological: 'Neurológico',
    skin: 'Pele',
}

const smokingLabels: Record<string, string> = {
    never: 'Nunca fumou',
    former: 'Ex-tabagista',
    current: 'Tabagista atual',
}

const alcoholLabels: Record<string, string> = {
    never: 'Não consome',
    social: 'Social',
    regular: 'Regular',
    heavy: 'Pesado',
}

const activityLabels: Record<string, string> = {
    none: 'Sedentário',
    occasional: 'Ocasional',
    regular: 'Regular',
    intense: 'Intensa',
}

const reliabilityLabels: Record<string, string> = {
    alta: 'Alta',
    media: 'Média',
    baixa: 'Baixa',
}

function InfoItem({ label, value }: { label: string; value?: string | null }) {
    if (!value) return null
    return (
        <div>
            <p className="text-xs font-semibold text-base-content/60 mb-1">{label}</p>
            <p className="text-sm text-base-content whitespace-pre-wrap">{value}</p>
        </div>
    )
}

function hasContent(obj: any): boolean {
    if (!obj || typeof obj !== 'object') return false
    return Object.values(obj).some(v => {
        if (typeof v === 'string') return v.trim().length > 0
        if (typeof v === 'object' && v !== null) {
            if ('status' in v) return v.status && v.status !== 'never' && v.status !== 'none'
            if ('details' in v) return typeof v.details === 'string' && v.details.trim().length > 0
            return hasContent(v)
        }
        return false
    })
}

export default function AnamneseDetailPage() {
    const navigate = useProntuarioNavigate()
    const { clientId, anamneseId } = useProntuarioParams<{ clientId: string; anamneseId: string }>()

    const [anamnesis, setAnamnesis] = useState<Anamnesis | null>(null)
    const [loading, setLoading] = useState(true)

    const basePath = `/person/clients/${clientId}/prontuario`

    useEffect(() => {
        if (anamneseId) {
            loadAnamnesis()
        }
    }, [anamneseId])

    const loadAnamnesis = async () => {
        if (!anamneseId) return

        setLoading(true)
        try {
            const response = await getAnamnesis(parseInt(anamneseId, 10))
            if (response.success && response.result) {
                setAnamnesis(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar anamnese')
                navigate(`${basePath}/anamneses`)
            }
        } catch {
            toast.error('Erro ao carregar anamnese')
            navigate(`${basePath}/anamneses`)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando anamnese...</p>
            </div>
        )
    }

    if (!anamnesis) return null

    const systemsReview = anamnesis.systemsReview as Record<string, string> | null
    const physicalExam = anamnesis.physicalExam as Record<string, string> | null
    const lifeHabits = anamnesis.lifeHabits as Record<string, any> | null
    const socialConditions = anamnesis.socialConditions as Record<string, any> | null

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`${basePath}/anamneses`)}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-secondary/10 rounded-lg">
                            <MdAssignment className="text-2xl text-secondary" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-base-content">
                                Anamnese #{anamnesis.id}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {format(new Date(anamnesis.createdAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => navigate(`${basePath}/anamneses/${anamnesis.id}/editar`)}
                    className="btn btn-secondary btn-sm gap-2"
                >
                    <MdEdit className="text-lg" />
                    Editar
                </button>
            </div>

            {/* Professional & Template */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                <div className="flex flex-wrap items-center gap-4">
                    {anamnesis.professional && (
                        <div className="flex items-center gap-2 text-sm text-base-content">
                            <MdPerson className="text-base text-secondary" />
                            <span className="font-medium">
                                {anamnesis.professional.name}
                                {anamnesis.professional.registrationNumber && (
                                    <span className="text-base-content/60">
                                        {' '}- {anamnesis.professional.councilName} {anamnesis.professional.registrationNumber}
                                    </span>
                                )}
                            </span>
                        </div>
                    )}
                    {anamnesis.template && (
                        <div className="flex items-center gap-2">
                            <span className="badge badge-info badge-outline">
                                {anamnesis.template.name}
                            </span>
                            {anamnesis.template.specialty && (
                                <span className="badge badge-ghost">
                                    {MEDICAL_SPECIALTY_LABELS[anamnesis.template.specialty]}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Identification */}
            {(anamnesis.occupation || anamnesis.religion || anamnesis.informant || anamnesis.reliability) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Identificação</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoItem label="Ocupação" value={anamnesis.occupation} />
                        <InfoItem label="Religião" value={anamnesis.religion} />
                        <InfoItem label="Informante" value={anamnesis.informant} />
                        <InfoItem label="Confiabilidade" value={anamnesis.reliability ? reliabilityLabels[anamnesis.reliability] || anamnesis.reliability : undefined} />
                    </div>
                </div>
            )}

            {/* Chief Complaint */}
            {anamnesis.chiefComplaint && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Queixa Principal</h3>
                    <p className="text-sm text-base-content whitespace-pre-wrap">{anamnesis.chiefComplaint}</p>
                </div>
            )}

            {/* History of Present Illness */}
            {anamnesis.historyOfPresentIllness && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">História da Doença Atual (HDA)</h3>
                    <p className="text-sm text-base-content whitespace-pre-wrap">{anamnesis.historyOfPresentIllness}</p>
                </div>
            )}

            {/* Systems Review */}
            {systemsReview && hasContent(systemsReview) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Revisão de Sistemas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(systemsReviewLabels).map(([key, label]) => {
                            const value = systemsReview[key]
                            if (!value?.trim()) return null
                            return <InfoItem key={key} label={label} value={value} />
                        })}
                    </div>
                </div>
            )}

            {/* Life Habits */}
            {lifeHabits && hasContent(lifeHabits) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Hábitos de Vida</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {lifeHabits.smoking?.status && lifeHabits.smoking.status !== 'never' && (
                            <InfoItem label="Tabagismo" value={smokingLabels[lifeHabits.smoking.status] || lifeHabits.smoking.status} />
                        )}
                        {lifeHabits.alcohol?.status && lifeHabits.alcohol.status !== 'never' && (
                            <InfoItem label="Etilismo" value={alcoholLabels[lifeHabits.alcohol.status] || lifeHabits.alcohol.status} />
                        )}
                        {lifeHabits.physicalActivity?.status && lifeHabits.physicalActivity.status !== 'none' && (
                            <InfoItem label="Atividade Física" value={activityLabels[lifeHabits.physicalActivity.status] || lifeHabits.physicalActivity.status} />
                        )}
                        <InfoItem label="Alimentação" value={lifeHabits.diet} />
                        <InfoItem label="Sono" value={lifeHabits.sleep} />
                    </div>
                </div>
            )}

            {/* Social Conditions */}
            {socialConditions && hasContent(socialConditions) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Condições Sociais</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(socialConditions).map(([key, value]) => {
                            if (!value || (typeof value === 'string' && !value.trim())) return null
                            return <InfoItem key={key} label={key} value={String(value)} />
                        })}
                    </div>
                </div>
            )}

            {/* Physical Exam */}
            {physicalExam && hasContent(physicalExam) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Exame Físico</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(physicalExamLabels).map(([key, label]) => {
                            const value = physicalExam[key]
                            if (!value?.trim()) return null
                            return <InfoItem key={key} label={label} value={value} />
                        })}
                    </div>
                </div>
            )}

            {/* Diagnostic Hypothesis */}
            {anamnesis.diagnosticHypothesis && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Hipótese Diagnóstica</h3>
                    <p className="text-sm text-base-content whitespace-pre-wrap">{anamnesis.diagnosticHypothesis}</p>
                </div>
            )}

            {/* Footer info */}
            <div className="text-xs text-base-content/50 text-right pb-4">
                Última atualização: {format(new Date(anamnesis.updatedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </div>
        </div>
    )
}
